import { paymentReferenceTypes } from 'client/Interfaces/Draft/FieldsInterface'
import moment from 'moment'

import * as api from '../api/recurringProfile'
import { notifier } from '../components/common/Notifier'
import recurringKeys from '../components/txtkeys/recurringProfileKeys'
import { Analytics } from '../Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from '../Global/AnalyticsEvents'
import { DraftInterface } from '../Interfaces/Draft/DraftInterface'
import {
  RecurringProfileInterface,
  RecurringProfileSettingsInterface,
  RecurringProfileStatusEventType,
  RecurringProfileStatusType,
} from '../Interfaces/RecurringProfileInterface'
import { handleFieldsBeforeSave } from '../Other/functions/modelFunctions'
import { generateReferenceNumber, generateStructuredCommunication } from '../Other/functions/referenceNumberFunctions'
import { Immutable } from '../Other/Immutable'
import { RecurringProfileActions } from './actionTypes'
import { getCustomers } from './customer'
import { getInvoiceNumberSequences } from './documentNumber'
import {
  clearDraft,
  createInvoiceDraftAction,
  getCountry,
  loadVitalsForEditor,
  navigateBackToList,
  navigateToEditor,
  setTypeForInvoiceDraft,
  updateDraftCompanyFromCompany,
  updateDraftCompanyFromSettings,
  updateDraftCustomerAction,
  updateDraftFieldsFromSettings,
  updateDraftStylesFromSettings,
  updateInvoiceDraftAction,
  updateInvoiceNumber,
  updateReferenceNumber,
} from './draft'
import { actionCreator, actionCreatorAsync, AsyncAction } from './index'

export const updateRecurringProfileSettings = actionCreator<RecurringProfileSettingsInterface>(
  RecurringProfileActions.UPDATE_RECURRING_PROFILE_SETTINGS,
)
export const deleteRecurringProfileSettings = actionCreator(RecurringProfileActions.DELETE_RECURRING_PROFILE_SETTINGS)
export const updateRecurringProfileAction = actionCreator<RecurringProfileInterface>(
  RecurringProfileActions.UPDATE_RECURRING_PROFILE,
)

// this is used until/if we implement graphQL
export function getRecurringProfiles(): AsyncAction<RecurringProfileInterface[]> {
  return actionCreatorAsync<RecurringProfileInterface[]>(RecurringProfileActions.GET_RECURRING_PROFILES, async () => {
    return api.getRecurringProfiles()
  })
}

export function getRecurringProfile(id: number) {
  return async (dispatch, getState) => {
    const profile = await api.getRecurringProfile(id)
    profile.schedule.id = profile.id
    if (profile.status.nextScheduleDate) {
      const nextDate = moment(profile.status.nextScheduleDate)
      const dueDate = nextDate.add(profile.draft.fields.paymentTime.value, 'days')
      profile.draft.fields.issueDate.value = profile.status.nextScheduleDate
      profile.draft.fields.dueDate.value = dueDate.toISOString()
    }
    await dispatch(createInvoiceDraftAction(profile.draft))
    await dispatch(updateRecurringProfileSettings({ ...profile.schedule, status: profile.status.status }))
    return profile
  }
}

export function createRecurringProfile(profile: RecurringProfileInterface) {
  return async (dispatch, getState) => {
    profile.draft = handleFieldsBeforeSave(profile.draft, getState().settings)
    setTypeForInvoiceDraft(profile.draft, getState().config, getCountry(getState()))
    if (profile.draft && profile.draft.id === 0) {
      delete profile.draft.id
    }
    await dispatch(updateInvoiceDraftAction(profile.draft))
    const result = await api.createRecurringProfile(profile)
    profile.schedule.id = result.id
    notifier(recurringKeys.recurring.notification.create, 'success')
    return result
  }
}

export function updateRecurringProfile(profile: RecurringProfileInterface) {
  return async (dispatch, getState) => {
    profile.draft = handleFieldsBeforeSave(profile.draft, getState().settings)
    await dispatch(updateInvoiceDraftAction(profile.draft))
    await api.updateRecurringProfile(profile)
    notifier(recurringKeys.recurring.notification.save, 'success')
    dispatch(clearDraft)
    dispatch(updateRecurringProfileAction(profile))
  }
}

export function deleteRecurringProfile(id: number): AsyncAction<number> {
  Analytics.trackEvent(`${Events.deleteRecurringInvoice}`, {
    type: AnalyticsType.action,
    category: Category.recurringInvoice,
    result: Result.success,
    placement: Placement.invoices,
  })
  return actionCreatorAsync<number>(RecurringProfileActions.DELETE_RECURRING_PROFILE, async () =>
    api.deleteRecurringProfile(id),
  )
}

export function editRecurringProfile(id: number) {
  return async (dispatch, getState) => {
    Analytics.trackEvent(Events.openRecurringInvoice, {
      type: AnalyticsType.action,
      category: Category.recurringInvoice,
      result: Result.success,
      placement: Placement.invoices,
    })
    await loadVitalsForEditor(dispatch, getState())
    await dispatch(getRecurringProfile(id))
    await dispatch(getInvoiceNumberSequences())
    await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
    if (!getState().customers.collection) {
      await dispatch(getCustomers())
    }
    const state = getState()
    const draftReceiver = state.draft.receiver
    const allCustomers = Immutable.copy(state.customers.collection)
    const updatedCustomer = allCustomers.find((c) => c?.id === draftReceiver?.id) || draftReceiver
    dispatch(updateDraftFieldsFromSettings(state.settings))
    dispatch(updateDraftCustomerAction(updatedCustomer))
    dispatch(updateDraftCompanyFromSettings(state.settings))
    dispatch(updateDraftCompanyFromCompany(state.company))
    dispatch(updateDraftStylesFromSettings(state.settings))
    const fields = state.draft.fields
    if (
      fields.paymentReferenceType &&
      [paymentReferenceTypes.SPY_FI, paymentReferenceTypes.SPY_BE].includes(fields.paymentReferenceType.value) &&
      (!fields.referenceNumber || !fields.referenceNumber.value)
    ) {
      dispatch(
        updateReferenceNumber(
          fields.paymentReferenceType.value === paymentReferenceTypes.SPY_FI
            ? generateReferenceNumber(fields.invoiceNumber.value)
            : generateStructuredCommunication(),
        ),
      )
    }
    await navigateToEditor(dispatch, getState)
  }
}

export function setRecurringProfileStatus(id: number, status: RecurringProfileStatusEventType) {
  return async (dispatch, getState) => {
    const profile = await api.setRecurringProfileStatus(id, status)
    dispatch(updateRecurringProfileAction(profile))
  }
}

export function approveRecurringProfile(draft: DraftInterface, schedule: RecurringProfileSettingsInterface) {
  return async (dispatch, getState) => {
    const profile: RecurringProfileInterface = {
      draft,
      schedule,
    }
    delete schedule.status
    if (schedule.id) {
      profile.id = schedule.id
      await dispatch(updateRecurringProfile(profile))
    } else {
      await dispatch(createRecurringProfile(profile))
    }
    Analytics.trackEvent(Events.approveRecurringInvoice, {
      type: AnalyticsType.action,
      category: Category.recurringInvoice,
      result: Result.success,
      placement: Placement.preview,
      schedule: {
        id: profile.schedule.id,
        frequency: profile.schedule.frequency,
        action: profile.schedule.action,
        duration: profile.schedule.repeat ? 'limited' : 'indefinite',
        repeat: profile.schedule.repeat,
        notify: profile.schedule.confirmation,
      },
    })
    const stateProfile = getState().recurringProfile
    if (!profile.status || profile.status.status === RecurringProfileStatusType.DRAFT) {
      await dispatch(setRecurringProfileStatus(stateProfile.id, RecurringProfileStatusEventType.STARTED))
    }
    dispatch(updateRecurringProfileAction(stateProfile))
    dispatch(async (dispatch, getState) => {
      return navigateBackToList(dispatch, getState, draft)
    })
  }
}
